import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ActivationMethod,
  AndroidBrand,
  AnswerMode,
  CarrierId,
  DETECTED_CARRIER,
  DeviceOS,
  RuleId,
  codeSteps,
  detectOS,
  getCarrier,
  methodAvailability,
  ruleAvailable,
} from '../data/mobileForwarding'
import type { JourneyScreen, JourneyStart, VerificationState } from '../types/forwarding'
import { getDemoDevice, subscribeDemoDevice } from '../utils/demoDevice'

export type { JourneyScreen, VerificationState }

const CONDITIONAL_RULES: RuleId[] = ['busy', 'unanswered', 'unreachable']

/**
 * State for the mobile activation journey. With `start`, the journey resumes a
 * returning user's saved number, carrier, rules and progress instead of
 * starting from scratch.
 */
export function useMobileActivation(start?: JourneyStart) {
  const isReturning = Boolean(start)
  // Codes, phone-settings steps and confirmation all live on "Connect your phone" now.
  const rawEntry: JourneyScreen = start?.screen ?? 'mode'
  const entryScreen: JourneyScreen = ['code', 'settings', 'codeConfirm', 'settingsConfirm'].includes(rawEntry)
    ? 'method'
    : rawEntry

  const [os, setOs] = useState<DeviceOS>(getDemoDevice()?.os ?? start?.setup?.os ?? detectOS())
  const [androidBrand, setAndroidBrand] = useState<AndroidBrand>(
    getDemoDevice()?.androidBrand ?? start?.setup?.androidBrand ?? 'samsung',
  )
  const [carrierId, setCarrierId] = useState<CarrierId>(start?.setup?.carrierId ?? DETECTED_CARRIER)
  const [mode, setMode] = useState<AnswerMode>(start?.mode ?? 'conditional')
  const [rules, setRules] = useState<RuleId[]>(
    start && start.mode === 'conditional' && start.rules.length ? start.rules : CONDITIONAL_RULES,
  )
  // Returning users already agreed to the call-handling terms.
  const [consent, setConsent] = useState(isReturning)
  const [screen, setScreen] = useState<JourneyScreen>(entryScreen)
  const [method, setMethod] = useState<ActivationMethod>(start?.method ?? start?.setup?.method ?? 'code')
  const [failedMethod, setFailedMethod] = useState<ActivationMethod | null>(null)
  const [codeStepIdx, setCodeStepIdx] = useState(start?.codeStepIdx ?? 0)
  const [settingsIdx, setSettingsIdx] = useState(start?.settingsIdx ?? 0)
  const [verification, setVerification] = useState<Partial<Record<RuleId, VerificationState>>>({})

  const carrier = getCarrier(carrierId)
  const activeRules: RuleId[] = mode === 'all' ? ['all'] : rules
  const ctx = { carrier, os, mode, rules: activeRules }
  const codeAvail = methodAvailability('code', ctx)
  const settingsAvail = methodAvailability('settings', ctx)
  const steps = useMemo(() => codeSteps(carrier, mode, activeRules), [carrier, mode, activeRules.join()])
  // iPhone + "when I can't answer": Settings would forward every call, so it's never an equivalent fallback.
  const iosConditional = os === 'ios' && mode === 'conditional'

  // Drop rules the chosen carrier/device can't support (only once the user changes them).
  const firstRun = useRef(true)
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false
      return
    }
    setRules((prev) => prev.filter((r) => ruleAvailable(carrier, os, r)))
  }, [carrierId, os])

  // Demo Console phone switch — updates the open journey live.
  useEffect(
    () =>
      subscribeDemoDevice(() => {
        const d = getDemoDevice()
        if (!d) return
        setOs(d.os)
        setAndroidBrand(d.androidBrand)
        setSettingsIdx(0)

      }),
    [mode],
  )

  // "Answer all calls" defaults to phone settings (iPhone and Android) when
  // the phone supports it; the user can still pick the code instead.
  const defaultMethod = (): ActivationMethod => {
    if (mode === 'all' && settingsAvail.available) return 'settings'
    if (codeAvail.available) return 'code'
    return 'settings'
  }

  // Apply the default whenever the method screen opens, until the user picks
  // a method themselves for the current answering mode.
  const methodPicked = useRef(false)
  useEffect(() => {
    methodPicked.current = false
  }, [mode])
  useEffect(() => {
    if (screen === 'method' && !methodPicked.current) setMethod(defaultMethod())
  }, [screen, mode, os, carrierId, settingsAvail.available, codeAvail.available])
  const chooseMethod = (m: ActivationMethod) => {
    methodPicked.current = true
    setMethod(m)
  }

  const toggleRule = (r: RuleId) =>
    setRules((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]))

  const goToMethod = () => {
    setMethod(defaultMethod())
    setScreen('method')
  }

  // Open "Connect your phone" with a specific method selected (used by recovery).
  const startMethod = (m: ActivationMethod) => {
    methodPicked.current = true
    setMethod(m)
    setScreen('method')
  }

  const fail = (m: ActivationMethod) => {
    setFailedMethod(m)
    setScreen('fallback')
  }

  // The user reports they enabled forwarding (every code dialled, or settings
  // changed). This is user-reported only — Vexu doesn't verify the network.
  const confirmActivation = () => {
    setCodeStepIdx(Math.max(0, steps.length - 1))
    setVerification(Object.fromEntries(activeRules.map((r) => [r, 'verified' as VerificationState])))
    setScreen('ready')
  }

  const switchToAllCalls = () => {
    setMode('all')
    setCodeStepIdx(0)
    setSettingsIdx(0)
    setFailedMethod(null)
    const avail = methodAvailability('settings', { carrier, os, mode: 'all', rules: ['all'] })
    setMethod(avail.available ? 'settings' : 'code')
    setScreen('method')
  }



  return {
    isReturning,
    entryScreen,
    os, setOs,
    androidBrand, setAndroidBrand,
    carrier, carrierId, setCarrierId,
    mode, setMode,
    rules, toggleRule, activeRules,
    consent, setConsent,
    screen, setScreen,
    method, setMethod, chooseMethod, startMethod, goToMethod,
    codeAvail, settingsAvail, iosConditional,
    steps, codeStepIdx,
    settingsIdx, setSettingsIdx,
    failedMethod, fail,
    confirmActivation, switchToAllCalls,
    verification,
  }
}

export type MobileActivation = ReturnType<typeof useMobileActivation>
